
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import CustomModal from '../../Common/CustomModal';
import FormHeader from '../../Common/FormHeader';
import ViewTable from '../../Common/ViewTable';
import logo from '../../../assets/images/project-img/rites_logo.png';
import { updateLuggageRackFlowStatus } from '../../../store/page/furnishing/furnishingThunk';

const ViewLuggageRackModal = ({ toggle, isOpen, viewData, onSuccess }) => {
  const dispatch = useDispatch();
  const printRef = useRef();
  const [selectedAction, setSelectedAction] = useState(null);
  const [remark, setRemark] = useState('');
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const [submittingAction, setSubmittingAction] = useState(false);
  const user = JSON.parse(localStorage.getItem('user')) || {};
  const userRole = user?.role || '';
  const [freezeDate, setFreezeDate] = useState('');

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'Final_Inspection',
  });

  const handleTakeAction = async (actionType) => {
    if (!viewData?.id) {
      toast.error('Record id not found');
      return;
    }

    if (actionType === 'reinspection_needed' && !remark.trim()) {
      toast.error('Please enter reinspection reason');
      return;
    }

    setSubmittingAction(true);
    try {
      const result = await dispatch(
        updateLuggageRackFlowStatus({
          id: viewData.id,
          flow_status: actionType === 'reinspection_needed' ? 0 : 2,
          remark: actionType === 'reinspection_needed' ? remark : '',
          freeze_date: actionType === 'freeze' ? freezeDate : null,
        }),
      ).unwrap();
      console.log('Action result:', result);
      if (result?.status !== true) {
        toast.error(result?.message || 'Failed to submit action');
      } else {
        toast.success(result?.message || 'Action submitted successfully');
        setSelectedAction(null);
        setRemark('');
        setFreezeDate('');
        if (onSuccess) {
          onSuccess();
        }
        toggle();
      }
    } catch (error) {
      toast.error(error || 'Failed to submit action');
    } finally {
      setSubmittingAction(false);
    }
  };

  const displayListData = viewData;

  const normalizeStatus = (item) => {
    if (item?.status_key) return item.status_key;
    const status = String(item?.status ?? '').trim();
    if (status === '1') return 'OK';
    if (status === '2') return 'NOT OK';
    return status || '-';
  };


  const getInspectionRows = () => {
    if (Array.isArray(displayListData)) return displayListData;
    if (Array.isArray(displayListData?.inspection_items)) return displayListData.inspection_items;
    if (Array.isArray(displayListData?.items)) return displayListData.items;
    if (Array.isArray(displayListData?.data)) return displayListData.data;
    return [];
  };

  const tableRows = getInspectionRows();

  const canTakeAction =
    ['rites_hod',
      'mcf_furnishing_qa',
      'mcf_furnishing_qa_head',
      'furnishing_qa_head', 'admin'].includes(userRole) &&
    String(displayListData?.flow_status) !== '2';

  const formatText = (value) => {
    if (!value) return '';
    return value.replaceAll('_', ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const columns = [
    {
      key: 'description',
      label: 'Description',
    },
    {
      key: 'observation',
      label: 'Observation',
      render: (cell, row) => {
        const data = row.originalData;
        return (
          <div>
            <span>{data?.status_key || normalizeStatus(data)}</span>
            {data?.status === '2' && data?.image_url && (
              <button
                type="button"
                className="btn btn-sm btn-primary ms-2"
                onClick={() => window.open(data.image_url, '_blank')}
              >
                View
              </button>
            )}
          </div>
        );
      },
    },
    {
      key: 'location',
      label: 'Location',
    },
    {
      key: 'rectification_status',
      label: 'Rectified / Not OK (By Production)',
      render: (cell, row) => formatText(row.originalData?.rectification) || '-',
    },
    {
      key: 'remark',
      label: 'Remarks',
    },
  ];

  const tableData = tableRows.map((item, index) => ({
    id: index + 1,
    description: item?.description || '-',
    observation: item?.status_key || normalizeStatus(item),
    location: item?.location || '-',
    rectification_status: formatText(item?.rectification),
    remark: item?.remarks || item?.remark || '-',
    originalData: item,
  }));

  return (
    <CustomModal toggle={toggle} isOpen={isOpen} size="xl" title="View Details" backdrop="static">
      {/* PRINT BUTTON */}
      <div className="text-end mb-2">
        <button type="button" className="btn btn-primary" onClick={handlePrint}>
          Print PDF
        </button>
      </div>

      <div ref={printRef}>
        <FormHeader
          logo={logo}
          companyName="Modern Coach Factory Raebareli"
          title1="Quality Control Clearance Memo"
          title2="Stage Audit inspection – Luggage rack VB"
          docNo={`MCF/RBL/QMF/VB 7000D/${displayListData?.frame_type_name || ''}`}
          revNo="00"
          date="21/07/2025"
        />

        <div className="view-card">
         
          <div className="row mt-3">
            <div className="col-md-6">
              <p>
                <b>SHIFT :- </b>
                {displayListData?.shift_type?.name || displayListData?.shift || '-'}
              </p>
              <p>
                <b>SHELL NO :- </b>
                {displayListData?.shell_dtls?.form_no || '-'}
              </p>
              <p>
                <b>SHELL TYPE :- </b>
                {displayListData?.shell_type?.title || displayListData?.shell_type_name || '-'}
              </p>
            </div>
            <div className="col-md-6">
              <p>
                <b>DATE :- </b>
                {displayListData?.date
                  ? new Date(displayListData.date).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })
                  : '-'
                }
              </p>
              <p>
                <b>COACH NO :- </b>
                {displayListData?.coach_no || '-'}
              </p>

              {displayListData?.freeze_date && (
                <p>
                  <b>Freeze Date:- </b>
                  {new Date(displayListData.freeze_date).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                </p>
              )}
            </div>
          </div>          
          <h4 className="mt-4">1. Visual Observation</h4>
          <ViewTable data={tableData} columns={columns} search={false} pagination={false} />

          
          {displayListData?.other_obs && (
            <div className="mt-3">
              <p><strong>Other Observations:</strong></p>
              <p className="border rounded p-2 bg-light">{displayListData.other_obs}</p>
            </div>
          )}

      
          <div className="mt-3">
            <b>Confirming Status :-</b>{' '}
            <span className={`badge ${String(displayListData?.confirming) === '1' ? 'bg-success' : 'bg-danger'}`}>
              {String(displayListData?.confirming) === '1' ? 'Conforming' : String(displayListData?.confirming) === '0' ? 'Non-Conforming' : '-'}
            </span>
          </div>

          {displayListData?.remark && (
            <div className="mt-3">
              <p><strong>Reinspection Remark:</strong></p>
              <p className="border rounded p-2 bg-light">{displayListData.remark}</p>
            </div>
          )}



          {/* {canTakeAction && (
            <div className="mt-3 pt-3 border-top">
              <div className="fw-semibold mb-2">Take Action</div>
              <div className="d-flex align-items-start gap-2 flex-wrap">
                <Dropdown isOpen={actionDropdownOpen} toggle={() => setActionDropdownOpen((p) => !p)}>
                  <DropdownToggle caret color="info" style={{ fontSize: '14px' }}>
                    {selectedAction === 'reinspection_needed'
                      ? 'Reinspection Needed'
                      : selectedAction === 'freeze'
                        ? 'Freeze'
                        : 'Select Action'}
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem
                      onClick={() => {
                        setSelectedAction('reinspection_needed');
                      }}
                    >
                      Reinspection Needed
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => {
                        setSelectedAction('freeze');
                        setRemark('');
                      }}
                    >
                      Freeze
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>

                {selectedAction === 'reinspection_needed' && (
                  <>
                    <textarea
                      className="form-control"
                      style={{ width: '700px' }}
                      placeholder="Enter Reinspection reasons..."
                      value={remark}
                      aria-label="Remark"
                      onChange={(e) => setRemark(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-warning"
                      disabled={submittingAction || !remark.trim()}
                      onClick={() => handleTakeAction('reinspection_needed')}
                    >
                      Submit
                    </button>
                  </>
                )}

                {selectedAction === 'freeze' && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={submittingAction}
                    onClick={() => handleTakeAction('freeze')}
                  >
                    Confirm
                  </button>
                )}

                {selectedAction && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={submittingAction}
                    onClick={() => {
                      setSelectedAction(null);
                      setRemark('');
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          )} */}
          {canTakeAction && (
            <div className="mt-3 pt-3 border-top">
              <div className="fw-semibold mb-2">Take Action</div>
              <div className="d-flex align-items-start gap-2 flex-wrap">
                <Dropdown isOpen={actionDropdownOpen} toggle={() => setActionDropdownOpen((p) => !p)}>
                  <DropdownToggle caret color="info" style={{ fontSize: '14px' }}>
                    {selectedAction === 'reinspection_needed'
                      ? 'Reinspection Needed'
                      : selectedAction === 'freeze'
                        ? 'Freeze'
                        : 'Select Action'}
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem
                      onClick={() => {
                        setSelectedAction('reinspection_needed');
                        setFreezeDate('');
                      }}
                    >
                      Reinspection Needed
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => {
                        setSelectedAction('freeze');
                        setRemark('');
                      }}
                    >
                      Freeze
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                {selectedAction === 'reinspection_needed' && (
                  <>
                    <textarea
                      className="form-control"
                      style={{ width: '700px' }}
                      placeholder="Enter Reinspection reasons..."
                      value={remark}
                      aria-label="Remark"
                      onChange={(e) => setRemark(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-warning"
                      disabled={submittingAction}
                      onClick={() => handleTakeAction('reinspection_needed', remark)}
                    >
                      Submit
                    </button>
                  </>
                )}
                {selectedAction === 'freeze' && (
                  <>
                    <input
                      type="date"
                      className="form-control"
                      style={{ width: '220px' }}
                      value={freezeDate}
                      onChange={(e) => setFreezeDate(e.target.value)}
                      // min={displayListData?.date || ''}
                      required
                    />
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={submittingAction}
                      onClick={() => handleTakeAction('freeze', '', freezeDate)}
                    >
                      Confirm Freeze
                    </button>
                  </>
                )}
                {selectedAction && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={submittingAction}
                    onClick={() => {
                      setSelectedAction(null);
                      setRemark('');
                      setFreezeDate('');
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          )}


          <div className="mt-5">
            <p>
              <b>Inspector Name :-</b> {displayListData?.inspect_user?.name || displayListData?.inspect_by || '-'}
            </p>
            <p>
              <b>Designation :-</b> {displayListData?.inspect_user?.designation || '-'}
            </p>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end">
        <Button color="secondary" type="button" onClick={toggle}>
          Close
        </Button>
      </div>
    </CustomModal>
  );
};

ViewLuggageRackModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  viewData: PropTypes.object,
  onSuccess: PropTypes.func,
};
ViewLuggageRackModal.defaultProps = {
  viewData: null,
  onSuccess: null,
};
export default ViewLuggageRackModal;